import * as stylex from '@stylexjs/stylex';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { FormDevtools as FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import { HotkeysProvider } from '@tanstack/react-hotkeys';
import { type QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, HeadContent, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { useMediaQuery } from '@uidotdev/usehooks';
import {
  useEffect,
  useRef,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { UNSAFE_PortalProvider as PortalProvider } from 'react-aria';
import { I18nProvider, RouterProvider, type UNSTABLE_ToastQueue as ToastQueue } from 'react-aria-components';

import { ErrorState } from '@/components/error-state.component';
import { NotFoundState } from '@/components/not-found-state.component';
import { LOCALE } from '@/constants/locale';
import { useSWUpdatePrompt } from '@/hooks/use-sw-update-prompt.hook';
import {
  Icon,
  IconButton,
  Text,
  type TextButtonProps,
  type SnackbarProps,
  TextButton,
  ToastRegion,
  Toast,
  Snackbar,
  TOAST_SLOTS,
} from '@/libs/serenity-ui';
import { dark } from '@/libs/serenity-ui/theme';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import type { organizationQueries } from '@/resources/organization/organization.api';
import type { userQueries } from '@/resources/user/user.api';
import type { QueryResultData } from '@/types/query-result-data.type';
import { Theme } from '@/types/theme.type';

export interface RouterContext {
  queryClient: QueryClient;
  toastQueue: ToastQueue<{ message: string; actionProps?: TextButtonProps; variant: SnackbarProps['variant'] }>;
  user: Exclude<QueryResultData<typeof userQueries.me>, undefined>; // A case where the user is undefined is handled in the main.
  organization: QueryResultData<typeof organizationQueries.me>;
  theme: Theme;
  setTheme: Dispatch<SetStateAction<Theme>>;
  /** When set, the `_main` layout redirects to `/info` and renders this content. */
  info?: { heading: string; details: ReactNode };
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RouteComponent,
  errorComponent: ErrorState,
  notFoundComponent: NotFoundState,
});

function RouteComponent() {
  const routeContext = Route.useRouteContext();
  const navigate = Route.useNavigate();

  useSWUpdatePrompt();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const shouldUseDarkMode = routeContext.theme === Theme.DARK || (routeContext.theme === Theme.AUTO && prefersDarkMode);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');

    if (meta) {
      meta.setAttribute('content', shouldUseDarkMode ? '#09090AFF' : '#FFFFFFFF');
    }
  }, [ shouldUseDarkMode ]);

  return (
    <PortalProvider getContainer={() => containerRef.current}>
      <I18nProvider locale={LOCALE}>
        <RouterProvider navigate={(path, options) => navigate({ to: path, ...(options ?? {}) })}>
          <HeadContent />
          <ToastRegion queue={routeContext.toastQueue}>
            {(renderProps) => (
              <Toast toast={renderProps.toast}>
                <Snackbar variant={renderProps.toast.content.variant}>
                  <Icon />
                  <Text>{renderProps.toast.content.message}</Text>
                  {renderProps.toast.content.actionProps && <TextButton {...renderProps.toast.content.actionProps} />}
                  <IconButton slot={TOAST_SLOTS.CLOSE} />
                </Snackbar>
              </Toast>
            )}
          </ToastRegion>

          <HotkeysProvider>
            <div id="app" ref={containerRef} {...stylex.props(shouldUseDarkMode && dark, styles.base)}>
              <Outlet />
            </div>
          </HotkeysProvider>

          <TanStackDevtools
            config={{ hideUntilHover: true, position: 'middle-right' }}
            plugins={[
              {
                name: 'Tanstack Query',
                render: <ReactQueryDevtoolsPanel />,
              },
              {
                name: 'Tanstack Router',
                render: <TanStackRouterDevtoolsPanel />,
              },
              {
                name: 'TanStack Form',
                render: <FormDevtoolsPanel />,
              },
            ]}
          />
        </RouterProvider>
      </I18nProvider>
    </PortalProvider>
  );
}

const styles = stylex.create({
  base: {
    backgroundColor: $.surfaceBGPrimary,
    overflow: 'hidden',
  },
});
